import { defineStore } from 'pinia';
import { ref } from 'vue';
import { createShipmentFormMetadataSlice } from '@/stores/shipment-form/metadata-slice';
import { createShipmentFormDevicesSlice } from '@/stores/shipment-form/devices-slice';
import { createShipmentFormRouteComposerSlice } from '@/stores/shipment-form/route-composer-slice';
import { createShipmentFormHydrationSlice } from '@/stores/shipment-form/hydration-slice';
import { createShipmentFormStopsSlice } from '@/stores/shipment-form/stops-slice';

export type {
  ShipmentFormSelectedDevice,
  ShipmentFormStopRow,
} from '@/stores/shipment-form/stop-row';

export const useShipmentFormStore = defineStore('shipmentComposer', () => {
  const shipmentId = ref<number | null>(null);

  const metadata = createShipmentFormMetadataSlice();
  const devices = createShipmentFormDevicesSlice(metadata.generalName);
  const routeComposer = createShipmentFormRouteComposerSlice();
  const hydration = createShipmentFormHydrationSlice();
  const stopsSlice = createShipmentFormStopsSlice(
    routeComposer,
    {
      stopsSyncedOnDeviceApi: hydration.stopsSyncedOnDeviceApi,
      resetHydrationFlags: hydration.resetHydrationFlags,
    },
    metadata.generalPlannedRange,
  );

  function resetFormState() {
    routeComposer.resetRouteComposerState();
  }

  function resetStops() {
    stopsSlice.resetStops();
    metadata.persistedMetadataSnapshot.value = null;
    devices.persistedDeviceIdsSnapshot.value = null;
  }

  function resetForm() {
    shipmentId.value = null;
    metadata.resetMetadata();
    devices.resetDevices();
    resetStops();
  }

  function syncGeneralPlannedRangeFromStops() {
    metadata.syncGeneralPlannedRangeFromStops(stopsSlice.stops.value);
  }

  function applyGeneralPlannedRangeToAnchorStops() {
    metadata.applyGeneralPlannedRangeToAnchorStops(
      stopsSlice.stops.value,
      stopsSlice.patchStop,
      stopsSlice.markAnchorPlannedSlotsManual,
      stopsSlice.unmarkAnchorPlannedSlotsManual,
    );
  }

  return {
    stops: stopsSlice.stops,
    shipmentId,
    generalName: metadata.generalName,
    generalCarrier: metadata.generalCarrier,
    generalNotes: metadata.generalNotes,
    generalAssetValueCents: metadata.generalAssetValueCents,
    generalPlannedRange: metadata.generalPlannedRange,
    manualPlannedSlots: stopsSlice.manualPlannedSlots,
    serverFieldErrors: hydration.serverFieldErrors,
    routePlan: routeComposer.routePlan,
    corridorBufferM: routeComposer.corridorBufferM,
    geometryQualityPercent: routeComposer.geometryQualityPercent,
    geoCache: routeComposer.geoCache,
    serverHydratedRouteFingerprint:
      routeComposer.serverHydratedRouteFingerprint,
    formHydrationSuspended: hydration.formHydrationSuspended,
    stopsSyncedOnDeviceApi: hydration.stopsSyncedOnDeviceApi,
    serverRoutesEmptyAtHydrate: hydration.serverRoutesEmptyAtHydrate,
    persistedStopsPayloadSnapshot: stopsSlice.persistedStopsPayloadSnapshot,
    persistedMetadataSnapshot: metadata.persistedMetadataSnapshot,
    persistedDeviceIdsSnapshot: devices.persistedDeviceIdsSnapshot,
    persistedRouteComposerSnapshot:
      routeComposer.persistedRouteComposerSnapshot,
    selectedDevices: devices.selectedDevices,
    nameAutofillSourceDeviceId: devices.nameAutofillSourceDeviceId,
    nameAutofillBaseline: devices.nameAutofillBaseline,
    primaryDeviceId: devices.primaryDeviceId,
    selectedDeviceIds: devices.selectedDeviceIds,
    markMetadataPersisted: metadata.markMetadataPersisted,
    haveMetadataChangedSincePersisted: metadata.haveMetadataChangedSincePersisted,
    markDeviceIdsPersisted: devices.markDeviceIdsPersisted,
    haveDeviceIdsChangedSincePersisted: devices.haveDeviceIdsChangedSincePersisted,
    setSelectedDevices: devices.setSelectedDevices,
    clearSelectedDevices: devices.clearSelectedDevices,
    isNameAutofillUntouched: devices.isNameAutofillUntouched,
    setNameAutofillFromDevice: devices.setNameAutofillFromDevice,
    clearNameAutofillState: devices.clearNameAutofillState,
    markStopsPayloadPersisted: stopsSlice.markStopsPayloadPersisted,
    haveStopsChangedSincePersisted: stopsSlice.haveStopsChangedSincePersisted,
    markRouteComposerPersisted: routeComposer.markRouteComposerPersisted,
    haveRouteComposerChangedSincePersisted:
      routeComposer.haveRouteComposerChangedSincePersisted,
    resetStops,
    resetForm,
    resetFormState,
    clearServerHydratedRouteFingerprint:
      routeComposer.clearServerHydratedRouteFingerprint,
    markRoutePlanSyncedWithCurrentStops:
      routeComposer.markRoutePlanSyncedWithCurrentStops,
    suspendFormHydration: hydration.suspendFormHydration,
    resumeFormHydration: hydration.resumeFormHydration,
    applyPersistedStopIdsFromPosted: stopsSlice.applyPersistedStopIdsFromPosted,
    clearServerFieldErrors: hydration.clearServerFieldErrors,
    setServerFieldErrors: hydration.setServerFieldErrors,
    markServerRoutesPersistedFromForm: hydration.markServerRoutesPersistedFromForm,
    patchStop: stopsSlice.patchStop,
    setStopResolved: stopsSlice.setStopResolved,
    setStopGeofence: stopsSlice.setStopGeofence,
    setStopRadius: stopsSlice.setStopRadius,
    setStopPlannedArrival: stopsSlice.setStopPlannedArrival,
    setStopPlannedDeparture: stopsSlice.setStopPlannedDeparture,
    syncGeneralPlannedRangeFromStops,
    applyGeneralPlannedRangeToAnchorStops,
    markPlannedSlotManual: stopsSlice.markPlannedSlotManual,
    unmarkPlannedSlotManual: stopsSlice.unmarkPlannedSlotManual,
    markAnchorPlannedSlotsManual: stopsSlice.markAnchorPlannedSlotsManual,
    unmarkAnchorPlannedSlotsManual: stopsSlice.unmarkAnchorPlannedSlotsManual,
    clearManualPlannedSlots: stopsSlice.clearManualPlannedSlots,
    addWaypoint: stopsSlice.addWaypoint,
    removeStopAt: stopsSlice.removeStopAt,
    moveStop: stopsSlice.moveStop,
  };
});
